import {
  DEBOUNCE_TEXT_INPUT_CHANGES_DEFAULT,
  useDebounce,
} from '@ipfabric/ui-components/hooks/useDebounce';
import { useLoadingState } from '@ipfabric/ui-components/hooks/useLoadingState';
import { useStableCallback } from '@ipfabric/ui-components/hooks/useStableCallback';
import { withLoadingIndicator } from '@ipfabric/ui-components/utils/loading/withLoadingIndicator';
import type { Awaitable } from '@ipfabric/ui-components/utils/types/Awaitable';
import { useEffect, useState } from 'react';

interface AutocompleteOptions<TValue> {
  inputValue: string;
  handleInputValueChange: (value: string) => void;
  loadingData: boolean;
  loadedData: TValue[];
  /**
   * Function to load next page. Is returned only if there is next page to be loaded
   */
  loadNextPage?: () => void;
  reload: () => void;
}

interface AutocompleteOptionsProps<TValue> {
  load: (
    search: string,
    page: number,
  ) => Awaitable<{ data: TValue[]; count: number }>;

  debounceInterval?: number;
}

/**
 * Hook wrapping common logic used for infinite scroll, primarily
 * targeted for inputs with autocomplete function.
 * Pagination logic, total count & loading state are encapsulated in hook. Load function
 * have to correctly transform page & search string to limit & filter by concrete
 * feature needs.
 *
 * Usage example:
 *
 * const autocompleteOptions = useAutocompleteOptions({load: makeApiConnectedLoader()});
 *
 * <Select
 *   inputValue={autocompleteOptions.inputValue}
 *   onInputValueChange={autocompleteOptions.handleInputValueChange}
 *   onScrollBottom={autocompleteOptions.loadNextPage}
 *   options={autocompleteOptions.loadedData}
 *   {...otherProps}
 *  />
 */
export const useAutocompleteOptions = <TValue,>(
  props: AutocompleteOptionsProps<TValue>,
): AutocompleteOptions<TValue> => {
  const [inputValue, setInputValue] = useState<string>('');
  const [loadingState, setLoadingState] = useLoadingState();
  const [page, setPage] = useState<number>(0);
  const [total, setTotal] = useState<number | null>(null);
  const [loadedData, setLoadedData] = useState<TValue[]>([]);
  const [allLoaded, setAllLoaded] = useState<boolean>(false);
  const debounce = useDebounce(
    props.debounceInterval ?? DEBOUNCE_TEXT_INPUT_CHANGES_DEFAULT,
  );

  const handleInputChange = useStableCallback((value: string) => {
    setInputValue(value);
  });

  const loadNextPage = useStableCallback(async () => {
    if (loadingState === 'LOADING') {
      return;
    }

    if (total !== null && total <= loadedData.length) {
      setAllLoaded(true);
      return;
    }

    setPage(page + 1);
    const result = await withLoadingIndicator(setLoadingState)(() =>
      props.load(inputValue, page + 1),
    );
    setLoadedData([...loadedData, ...result.data]);
    setTotal(result.count);
  });

  const loadFirstPage = useStableCallback(async () => {
    setPage(0);
    setLoadedData([]);
    setAllLoaded(false);

    const result = await withLoadingIndicator(setLoadingState)(() =>
      props.load(inputValue, 0),
    );
    setLoadedData(result.data);
    setTotal(result.count);
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: reload on input change
  useEffect(() => {
    debounce(() => {
      loadFirstPage();
    });
  }, [inputValue]);

  return {
    inputValue,
    handleInputValueChange: handleInputChange,
    loadingData: loadingState === 'LOADING',
    loadedData,
    loadNextPage: allLoaded ? undefined : loadNextPage,
    reload: loadFirstPage,
  };
};
